import type { MapPin, ScheduleItem } from '../types';
import { mapsLinks } from '../lib/mapsLinks';
import { pinMayHavePhoto } from '../lib/googlePlaces';
import { useResolvedPinPhoto } from '../hooks/usePlacePhotos';

interface Props {
  pin?: Pick<
    MapPin,
    'id' | 'imageUrl' | 'imageAlt' | 'imageCredit' | 'name' | 'lat' | 'lng' | 'mapsQuery' | 'placeId'
  > | null;
  imageUrl?: string;
  imageAlt?: string;
  imageCredit?: string;
  className?: string;
  compact?: boolean;
  /** When true and pin has coords, wrap photo in a Maps link (primary = Apple on iOS). */
  linkToMaps?: boolean;
}

export function PlacePhoto({
  pin,
  imageUrl,
  imageAlt,
  imageCredit,
  className = '',
  compact = false,
  linkToMaps = false,
}: Props) {
  const resolved = useResolvedPinPhoto(pin);
  // Explicit overrides win; else Places (preferred) then Commons via hook.
  const url = imageUrl ?? resolved.url ?? pin?.imageUrl;
  if (!url) return null;
  const alt = imageAlt ?? resolved.alt;
  const credit = imageCredit ?? resolved.credit ?? pin?.imageCredit;

  const canLink =
    linkToMaps &&
    pin &&
    typeof pin.lat === 'number' &&
    typeof pin.lng === 'number' &&
    Number.isFinite(pin.lat) &&
    Number.isFinite(pin.lng);

  const href = canLink
    ? mapsLinks({
        name: pin!.name,
        lat: pin!.lat,
        lng: pin!.lng,
        mapsQuery: pin!.mapsQuery,
        placeId: pin!.placeId,
      }).primary
    : undefined;

  const img = (
    <img
      src={url}
      alt={alt}
      loading="lazy"
      decoding="async"
      onError={resolved.source === 'places' ? resolved.onError : undefined}
    />
  );

  return (
    <figure className={`place-photo${compact ? ' compact' : ''}${href ? ' is-maps-link' : ''} ${className}`.trim()}>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="place-photo-link"
          title={`Open ${pin?.name ?? 'place'} in Maps`}
          aria-label={`Open ${pin?.name ?? 'place'} in Maps`}
        >
          {img}
        </a>
      ) : (
        img
      )}
      {credit && <figcaption className="place-photo-credit">{credit}</figcaption>}
    </figure>
  );
}

/**
 * Match a schedule item to a pin that may carry a photo.
 * Exact pinId / id / title only — no fuzzy or "first coffee" fallbacks.
 */
export function findPinForScheduleItem(
  item: Pick<ScheduleItem, 'id' | 'title' | 'pinId'>,
  _dayPins: MapPin[],
  allPins: MapPin[],
): MapPin | undefined {
  if (item.pinId) {
    const byPinId = allPins.find((p) => p.id === item.pinId);
    if (byPinId) return byPinId;
  }

  const byExactId = allPins.find(
    (p) => p.id === `pin-${item.id}` || p.id === item.id,
  );
  if (byExactId) return byExactId;

  const title = item.title.trim().toLowerCase();
  if (!title) return undefined;
  return allPins.find((p) => p.name.trim().toLowerCase() === title);
}

export { pinMayHavePhoto };
